// filepath: frontend/src/App.tsx
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/store/authStore';
import { Skeleton } from '@/components/ui/Skeleton';
import { Role } from '@/types';

// Auth pages
const Login = lazy(() => import('@/pages/auth/Login'));
const Signup = lazy(() => import('@/pages/auth/Signup'));
const Setup2FA = lazy(() => import('@/pages/auth/Setup2FA'));
const GoogleCallback = lazy(() => import('@/pages/auth/GoogleCallback'));
const EmailCallback = lazy(() => import('@/pages/EmailCallback'));
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));

// Hub + Module dashboards (standalone, no AppShell)
const Hub = lazy(() => import('@/pages/hub/Hub'));
const BrokerageDashboard = lazy(() => import('@/pages/modules/BrokerageDashboard'));
const BrokerageEntryDetail = lazy(() => import('@/pages/modules/BrokerageEntryDetail'));
const CustomsEntryDetail = lazy(() => import('@/pages/modules/CustomsEntryDetail'));
const ControlTowerDashboard = lazy(() => import('@/pages/modules/ControlTowerDashboard'));
const ControlTowerDetail = lazy(() => import('@/pages/modules/ControlTowerDetail'));
const TttBoard = lazy(() => import('@/pages/hub/TttBoard'));

// Client portal (external customers — placeholder until the portal ships)
const ClientPortal = lazy(() => import('@/pages/portal/ClientPortal'));

// Shared
const ShipmentDetailRouter = lazy(() => import('@/pages/shared/ShipmentDetailRouter'));

// Filing
const FilingDashboard = lazy(() => import('@/pages/filing/FilingDashboard'));
const AsycudaWorkspace = lazy(() => import('@/pages/filing/AsycudaWorkspace'));
const FilingTariff = lazy(() => import('@/pages/filing/TariffLookup'));
const ComplianceDesk = lazy(() => import('@/pages/compliance/ComplianceDesk'));
const RunnerQueue = lazy(() => import('@/pages/runner/RunnerQueue'));
const EmailSettings = lazy(() => import('@/pages/settings/EmailSettings'));

// Account Manager
const AccountDashboard = lazy(() => import('@/pages/account/AccountDashboard'));
const AccountManagerWorkbench = lazy(() => import('@/pages/account/AccountManagerWorkbench'));

// Accounts
const AccountsDashboard = lazy(() => import('@/pages/accounts/AccountsDashboard'));
const Invoicing = lazy(() => import('@/pages/accounts/Invoicing'));

// Help
const HelpCenter = lazy(() => import('@/pages/help/HelpCenter'));
const HelpArticle = lazy(() => import('@/pages/help/HelpArticlePage'));

// Additional pages
const AccountJira = lazy(() => import('@/pages/account/JiraTickets'));
const AccountShipments = lazy(() => import('@/pages/account/ShipmentOversight'));
const AccountShipmentDetail = lazy(() => import('@/pages/account/ShipmentDetail'));
const AccountContainers = lazy(() => import('@/pages/account/ContainerManagement'));
const AccountsCosting = lazy(() => import('@/pages/accounts/CostingSheet'));
const AccountsAging = lazy(() => import('@/pages/accounts/AgingReport'));
const AccountsInvoiceDetail = lazy(() => import('@/pages/accounts/InvoiceDetail'));

const PageLoader = () => (
  <div className="p-6">
    <Skeleton lines={5} />
  </div>
);

// Where a user belongs when they land somewhere they shouldn't (or on "/").
const homeFor = (role: Role) => (role === 'client' ? '/portal' : '/hub');

function RequireAuth({ children, roles }: { children: React.ReactNode; roles?: Role[] }) {
  // Role gating is live again now that external `client` logins exist: a route
  // that names `roles` bounces everyone else to their own home. Routes without
  // `roles` stay auth-only (internal AppShell pages — the backend requireStaff
  // gate makes them useless to clients anyway).
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}

function DefaultRedirect() {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/magic-link" element={<Login />} />
            <Route path="/admin/dashboard" element={<Navigate to="/login" replace />} />
            <Route path="/auth/callback" element={<GoogleCallback />} />
            <Route path="/auth/2fa/setup" element={<Setup2FA />} />
            <Route path="/email-callback" element={<EmailCallback />} />

            {/* Client portal (external customers, no AppShell) */}
            <Route path="/portal" element={<RequireAuth roles={['client']}><ClientPortal /></RequireAuth>} />

            {/* Hub + Module shells (protected, no AppShell) */}
            <Route path="/hub" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><Hub /></RequireAuth>} />
            <Route path="/hub/control-tower" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><ControlTowerDashboard /></RequireAuth>} />
            <Route path="/hub/control-tower/:id" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><ControlTowerDetail /></RequireAuth>} />
            <Route path="/hub/ttt" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'ceo']}><TttBoard /></RequireAuth>} />
            <Route path="/hub/brokerage" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><BrokerageDashboard /></RequireAuth>} />
            <Route path="/hub/brokerage/control-tower" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><ControlTowerDashboard /></RequireAuth>} />
            <Route path="/hub/brokerage/entries/:id" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><CustomsEntryDetail /></RequireAuth>} />
            <Route path="/hub/brokerage/:id" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'accounts', 'ceo', 'document_runner']}><BrokerageEntryDetail /></RequireAuth>} />

            {/* Protected shell */}
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <AppShell>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route index element={<DefaultRedirect />} />

                        {/* Internal shipment views */}
                        <Route path="shipments" element={<AccountShipments />} />
                        <Route path="shipments/:id" element={<ShipmentDetailRouter />} />

                        {/* Filing */}
                        <Route path="filing/dashboard" element={<RequireAuth roles={['filing_clerk']}><FilingDashboard /></RequireAuth>} />
                        <Route path="filing/asycuda/:id?" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'ceo']}><AsycudaWorkspace /></RequireAuth>} />
                        <Route path="filing/tariff" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'ceo']}><FilingTariff /></RequireAuth>} />
                        <Route path="filing/compliance" element={<RequireAuth roles={['filing_clerk', 'account_manager', 'ceo']}><ComplianceDesk /></RequireAuth>} />
                        <Route path="runner/queue" element={<RequireAuth roles={['document_runner', 'account_manager', 'ceo']}><RunnerQueue /></RequireAuth>} />
                        <Route path="settings/email" element={<RequireAuth><EmailSettings /></RequireAuth>} />

                        {/* Account Manager */}
                        <Route path="account/dashboard" element={<RequireAuth roles={['account_manager']}><AccountDashboard /></RequireAuth>} />
                        <Route path="account/workbench" element={<RequireAuth roles={['account_manager', 'ceo']}><AccountManagerWorkbench /></RequireAuth>} />
                        <Route path="account/jira" element={<RequireAuth roles={['account_manager', 'ceo']}><AccountJira /></RequireAuth>} />
                        <Route path="account/shipments" element={<RequireAuth roles={['account_manager', 'ceo']}><AccountShipments /></RequireAuth>} />
                        <Route path="account/shipments/:id" element={<RequireAuth roles={['account_manager', 'ceo']}><AccountShipmentDetail /></RequireAuth>} />
                        <Route path="account/containers" element={<RequireAuth roles={['account_manager', 'ceo']}><AccountContainers /></RequireAuth>} />

                        {/* Accounts */}
                        <Route path="accounts/dashboard" element={<RequireAuth roles={['accounts']}><AccountsDashboard /></RequireAuth>} />
                        <Route path="accounts/invoicing" element={<RequireAuth roles={['accounts', 'ceo']}><Invoicing /></RequireAuth>} />
                        <Route path="accounts/invoicing/:id" element={<RequireAuth roles={['accounts', 'ceo']}><AccountsInvoiceDetail /></RequireAuth>} />
                        <Route path="accounts/costing" element={<RequireAuth roles={['accounts', 'ceo']}><AccountsCosting /></RequireAuth>} />
                        <Route path="accounts/aging" element={<RequireAuth roles={['accounts', 'account_manager', 'ceo']}><AccountsAging /></RequireAuth>} />

                        {/* Help */}
                        <Route path="help" element={<HelpCenter />} />
                        <Route path="help/:slug" element={<HelpArticle />} />

                        {/* Fallback */}
                        <Route path="*" element={<DefaultRedirect />} />
                      </Routes>
                    </Suspense>
                  </AppShell>
                </RequireAuth>
              }
            />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
